import React, { useState, useEffect, useRef } from "react";
import styles from "./ProfileSettings.module.css";
import Header from "../../Header/Header";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import { toast } from "react-toastify";
import config from "../../../config";
import { useProfile } from "../../../contexts/ProfileContext";
import { FaRegEdit, FaUserAlt } from "react-icons/fa";
import { RxCross1 } from "react-icons/rx";
import { IoCheckmarkOutline } from "react-icons/io5";
import PhoneInputField from "../../../components/PhoneInput/PhoneInput";
import { CiImageOn } from "react-icons/ci";
import { getApi } from "../../AppApis";
import { showErrorToast, showSuccessToast } from "../../utils";

function ProfileSettings() {
    const navigate = useNavigate();
    const { profileData, updateProfile, fetchProfile } = useProfile();
    const [isEditing, setIsEditing] = useState("");
    const [profilePic, setProfilePic] = useState(null);
    const fileInputRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // Profile data comes from context
    const [editableProfile, setEditableProfile] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "1",
        role: "",
    });
    const [profileImageUrl, setProfileImageUrl] = useState();
    const toastId = "profile-save-toast";

    useEffect(() => {
        if (!profileData) {
            fetchProfile();
        }
    }, [profileData]);

    useEffect(() => {
        const accessToken = localStorage.getItem("authUser");
        if (accessToken) {
            const token = accessToken.replace(/^"|"$/g, "");

            const profileUrl = `/user/profile`;
            getApi(profileUrl)
                .then((res) => {
                    const data = res.response.data || res.response;
                    // Ensure phone number is properly formatted for US
                    let formattedPhone = "";
                    if (data.phone) {
                        const digitsOnly = data.phone.replace(/\D/g, "");
                        if (digitsOnly.length === 10) {
                            formattedPhone = "1" + digitsOnly;
                        } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
                            formattedPhone = digitsOnly;
                        } else {
                            formattedPhone = data.phone.replace(/^\+/, "");
                        }
                    }

                    setEditableProfile({
                        firstName: data.first_name || "",
                        lastName: data.last_name || "",
                        email: data.email || "",
                        phone: formattedPhone || "1",
                        role: data.role || "",
                    });
                    if (data.profile_pic) {
                        setProfileImageUrl(
                            data.profile_pic.includes("http") ? data.profile_pic : `${config.get("IMAGE_BASE_URL")}/${data.profile_pic}`
                        );
                    }
                })
                .catch((error) => {
                    console.error("Error fetching profile:", error);
                });
        }
    }, []);

    const handleProfilePicClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleEditClick = (field) => {
        setIsEditing(field);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Allow only letters, spaces, and hyphens for names
        const isNameField = name === "firstName" || name === "lastName";
        const cleanedValue = isNameField ? value.replace(/[^a-zA-Z\s-]/g, "") : value;

        setEditableProfile((prevState) => ({
            ...prevState,
            [name]: cleanedValue,
        }));
    };

    const handlePhoneNumberChange = (value) => {
        // Ensure the phone number always starts with 1 (which shows as +1)
        let formattedValue = value;
        
        // Remove any non-digit characters to check the actual digits
        const digitsOnly = value.replace(/\D/g, "");
        
        // If user tries to remove the leading 1, add it back
        if (!digitsOnly.startsWith("1") && digitsOnly.length > 0) {
            formattedValue = "1" + digitsOnly;
        } else if (digitsOnly.length === 0) {
            // If user clears everything, default back to 1
            formattedValue = "1";
        } else {
            formattedValue = digitsOnly;
        }
        
        setEditableProfile((prevState) => ({
            ...prevState,
            phone: formattedValue,
        }));
    };

    const handleProfilePicChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePic(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfileImageUrl(e.target.result);
            };
            reader.readAsDataURL(file);
            handleProfilePicSave(file);
        }
    };

    const handleProfilePicSave = (file) => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append("profile_pic", file);

        updateProfile(formData)
            .then(() =>
                showSuccessToast("Profile picture updated successfully!", {
                    autoClose: 3000,
                    pauseOnHover: false,
                })
            )
            .catch(() =>
                showErrorToast("Failed to update profile picture", {
                    autoClose: 3000,
                    pauseOnHover: false,
                })
            );

        setIsLoading(false);
    };

    const handleProfileSave = () => {
        const formData = new FormData();
        if (!editableProfile.firstName) {
            return toast.error("Please enter first name", { toastId });
        }
        if (!editableProfile.lastName) {
            return toast.error("Please enter last name", { toastId });
        }
        if (!editableProfile.phone) {
            return toast.error("Please enter phone number", { toastId });
        }
        let phoneNumber = editableProfile.phone;
        // Ensure proper US phone number format for saving
        const digitsOnly = phoneNumber.replace(/\D/g, "");
        if (digitsOnly.length === 10) {
            phoneNumber = `+1${digitsOnly}`;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
            phoneNumber = `+${digitsOnly}`;
        } else if (!phoneNumber.startsWith("+")) {
            phoneNumber = `+${phoneNumber}`;
        }
        formData.append("first_name", editableProfile.firstName);
        formData.append("last_name", editableProfile.lastName || "");
        formData.append("phone", phoneNumber);
        formData.append("email", editableProfile.email);
        formData.append("role", editableProfile.role);

        if (profilePic) {
            formData.append("profile_pic", profilePic);
        }

        updateProfile(formData)
            .then(() => {
                showSuccessToast("Profile updated successfully!", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
                setIsEditing(null);
            })
            .catch((error) => {
                console.log(error);
                showErrorToast(`${error}`, {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
            });
    };

    const handleBlur = () => {
        handleProfileSave();
    };

    useEffect(() => {
        document.title = `Account Settings | ${config.get("COMPANY_NAME")}`;
    }, []);

    return (
        <>
            <Header title="Account Settings" />
            <div style={{ zIndex: 1 }} className={`tw-bg-background_color tw-overflow-y-auto tw-h-screen`}>
                <div className="tw-flex tw-w-full tw-flex-col tw-px-[20px] tw-mt-[70px]">
                    <div className="tw-flex tw-w-full tw-justify-between">
                        <h2 className="tw-text-[32px] tw-font-[600]">Settings</h2>
                    </div>
                    <p className="tw-text-[14px] tw-font-normal tw-flex tw-gap-2">
                        <span className="tw-text-gray-500">{"Setting >  "}</span> Profile Setting
                    </p>
                </div>
                <div className={"tw-flex tw-justify-center tw-items-center"}>
                    <div className={"tw-w-full"}>
                        <div className={styles.photoContainer}>
                            <div className={`${styles.avatarContainer}`}>
                                <span className="tw-w-fit tw-relative tw-rounded-full tw-bg-white">
                                    {profileImageUrl ? (
                                        <Avatar src={profileImageUrl} className="tw-m-2" alt="User profile" sx={{ width: 200, height: 200 }} />
                                    ) : (
                                        <div className="tw-m-2 tw-w-[200px] tw-h-[200px] tw-rounded-full tw-bg-primary tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-[80px]">
                                            {editableProfile.firstName?.charAt(0).toUpperCase()}
                                            {editableProfile.lastName?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <span
                                        onClick={handleProfilePicClick}
                                        className="tw-absolute tw-rounded-full -tw-bottom-0 -tw-right-0 tw-border-1 tw-border-white tw-bg-icon_background"
                                    >
                                        <CiImageOn className="tw-text-black tw-m-3 tw-text-[30px] tw-cursor-pointer" />
                                    </span>
                                </span>
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleProfilePicChange}
                                className={styles.hiddenFileInput}
                                style={{ display: "none" }}
                            />
                        </div>

                        <div style={{ zIndex: 1 }} className="tw-p-6 tw-rounded-lg tw-w-full">
                            <div className="tw-flex tw-flex-col lg:tw-flex-row tw-text-black tw-gap-6 tw-justify-between tw-w-full">
                                {/* Personal Information */}
                                <div className="tw-w-full lg:tw-w-1/2 tw-bg-white tw-p-4 tw-rounded-lg tw-shadow">
                                    <h2 className="tw-text-[18px] tw-font-semibold tw-mb-4">Personal Information</h2>

                                    {/* First Name Field */}
                                    <div className="tw-mb-4">
                                        <p className="tw-text-gray-800 tw-text-[16px] tw-mb-2">First Name</p>
                                        <div className="tw-flex tw-items-center tw-h-[45px] tw-border tw-rounded-lg tw-px-3 tw-py-2">
                                            {isEditing === "firstName" ? (
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    placeholder="Enter your first name"
                                                    value={editableProfile.firstName}
                                                    onChange={handleInputChange}
                                                    maxLength={50}
                                                    className="tw-flex-1 tw-border-none tw-outline-none"
                                                    autoFocus
                                                />
                                            ) : (
                                                <p className="tw-flex-1 tw-text-light_text tw-text-[12px]">{editableProfile.firstName}</p>
                                            )}
                                            <button className={styles.editButton} aria-label="Edit first name">
                                                {isEditing === "firstName" ? (
                                                    <div className="tw-flex tw-gap-2">
                                                        <RxCross1
                                                            className="tw-text-red-500"
                                                            onClick={() => {
                                                                setEditableProfile((prev) => ({
                                                                    ...prev,
                                                                    firstName: profileData?.first_name || "",
                                                                }));
                                                                setIsEditing(null);
                                                            }}
                                                        />
                                                        <IoCheckmarkOutline className="tw-text-green-500" onClick={handleBlur} />
                                                    </div>
                                                ) : (
                                                    <FaRegEdit onClick={() => handleEditClick("firstName")} />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Last Name Field */}
                                    <div className="tw-mb-4">
                                        <p className="tw-text-gray-800 tw-text-[16px] tw-mb-2">Last Name</p>
                                        <div className="tw-flex tw-items-center tw-h-[45px] tw-border tw-rounded-lg tw-px-3 tw-py-2">
                                            {isEditing === "lastName" ? (
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    placeholder="Enter your last name"
                                                    value={editableProfile.lastName}
                                                    onChange={handleInputChange}
                                                    maxLength={50}
                                                    className="tw-flex-1 tw-border-none tw-outline-none"
                                                    autoFocus
                                                />
                                            ) : (
                                                <p className="tw-flex-1 tw-text-light_text tw-text-[12px]">{editableProfile.lastName}</p>
                                            )}
                                            <button className={styles.editButton} aria-label="Edit last name">
                                                {isEditing === "lastName" ? (
                                                    <div className="tw-flex tw-gap-2">
                                                        <RxCross1
                                                            className="tw-text-red-500"
                                                            onClick={() => {
                                                                setEditableProfile((prev) => ({
                                                                    ...prev,
                                                                    lastName: profileData?.last_name || "",
                                                                }));
                                                                setIsEditing(null);
                                                            }}
                                                        />
                                                        <IoCheckmarkOutline className="tw-text-green-500" onClick={handleBlur} />
                                                    </div>
                                                ) : (
                                                    <FaRegEdit onClick={() => handleEditClick("lastName")} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <div className="tw-w-full lg:tw-w-1/2 tw-bg-white tw-p-4 tw-rounded-lg tw-shadow">
                                    <h2 className="tw-text-[18px] tw-font-semibold tw-mb-4">Contact Details</h2>

                                    {/* Phone Field */}
                                    <div className="tw-mb-4">
                                        <p className="tw-text-gray-800 tw-text-[16px] tw-mb-2">Phone Number</p>
                                        <div className="tw-flex tw-items-center tw-border tw-h-[50px] tw-rounded-lg tw-pr-3">
                                            {isEditing === "phone" ? (
                                                <div className="tw-w-full">
                                                    <PhoneInputField
                                                        name="phone"
                                                        phone={editableProfile.phone}
                                                        setPhone={handlePhoneNumberChange}
                                                        inputClass="tw-border-none tw-outline-none"
                                                        autoFocus
                                                    />
                                                </div>
                                            ) : (
                                                <p className="tw-flex-1 tw-mx-2 tw-text-light_text tw-text-[12px]">{editableProfile.phone}</p>
                                            )}
                                            <button className="tw-text-gray-500">
                                                {isEditing === "phone" ? (
                                                    <div className="tw-flex tw-gap-2">
                                                        <RxCross1
                                                            className="tw-text-red-500"
                                                            onClick={() => {
                                                                // Format the phone number when canceling edit
                                                                let formattedPhone = "";
                                                                if (profileData?.phone) {
                                                                    const digitsOnly = profileData?.phone?.replace(/\D/g, "");
                                                                    if (digitsOnly.length === 10) {
                                                                        formattedPhone = "1" + digitsOnly;
                                                                    } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
                                                                        formattedPhone = digitsOnly;
                                                                    } else {
                                                                        formattedPhone = profileData?.phone?.replace(/^\+/, "");
                                                                    }
                                                                }
                                                                setEditableProfile((prev) => ({
                                                                    ...prev,
                                                                    phone: formattedPhone,
                                                                }));
                                                                setIsEditing("");
                                                            }}
                                                        />
                                                        <IoCheckmarkOutline className="tw-text-green-500" onClick={handleBlur} />
                                                    </div>
                                                ) : (
                                                    <FaRegEdit onClick={() => setIsEditing("phone")} />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Email Field */}
                                    <div>
                                        <p className="tw-text-gray-800 tw-text-[16px] tw-mb-2">Email</p>
                                        <div className="tw-flex tw-items-center tw-h-[50px] tw-border tw-rounded-lg tw-px-3 tw-py-2">
                                            {isEditing === "email" ? (
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={editableProfile.email}
                                                    onChange={handleInputChange}
                                                    className="tw-flex-1 tw-border-none tw-outline-none"
                                                    autoFocus
                                                />
                                            ) : (
                                                <p className="tw-flex-1 tw-text-light_text tw-text-[12px]">{editableProfile.email}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ProfileSettings;
